import React, {useEffect, useState} from 'react'
import {
  Box,
  Stack,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material'
import {FormattedMessage} from 'react-intl'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import {STATUS} from 'src/constants/constants'
import {validateConsent} from './utils'
import LegalContent from 'src/components-new/layout/LegalContent'
import Card from 'src/components-new/layout/Card'
import Button from 'src/components-new/primitives/Button'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {HEADER_HEIGHT_DESKTOP, HEADER_HEIGHT_MOBILE, NEUTRAL_SCALE} from 'src/theme-new/tokens'

const CONSENT_OPTIONS = [
  {value: 'CONSENT', label: 'Esprimo il consenso'},
  {value: 'NOT_CONSENT', label: 'Nego il consenso'},
]

interface ConsentCheckboxOption {
  value: string
  label: string
  required: boolean
}

export default function ConsentsAcceptancePageNew(): JSX.Element {
  const userInfo = useAppSelector(s => s.userReducer.data)
  const pspConfig = useAppSelector(s => s.pspConfigReducer.data)
  const firstLogin = pspConfig.firstLoginConfiguration ?? {}
  const options: ConsentCheckboxOption[] = firstLogin.consentCheckboxOptions ?? []

  const [step, setStep] = useState(0)
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitStatus, setSubmitStatus] = useState<string>()
  const toast = useToast()

  const isSubmitting = submitStatus === STATUS.PENDING

  useEffect(() => {
    if (submitStatus === STATUS.REJECTED) {
      toast.error('Si è verificato un errore durante il salvataggio. Riprova.')
    }
  }, [submitStatus, toast])

  const validate = (): boolean => {
    const next: Record<string, string> = {}
    options.forEach(opt => {
      const error = validateConsent(opt, values[opt.value])
      if (error) next[opt.value] = error
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    const accepted = options
      .filter(opt => values[opt.value] === 'CONSENT')
      .map(opt => opt.value)
    const body = {privacyConsents: Array.from(new Set([...(userInfo.privacyConsents ?? []), ...accepted]))}
    setSubmitStatus(STATUS.PENDING)
    try {
      await apiClient.patchUserDataHttp(body)
      await apiClient.getProfile()
      setSubmitStatus(STATUS.FULFILLED)
    } catch (e) {
      setSubmitStatus(STATUS.REJECTED)
    }
  }

  const handleNext = () => {
    setStep(step + 1)
    window.scrollTo(0, 0)
  }

  const downloadCookieFile = async (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      const response = await apiClient.getCookieFile(firstLogin.cookieFile.file, () => {})
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = firstLogin.cookieFile.name || 'cookie-policy.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error downloading cookie policy:', err)
    }
  }

  const logoSrc = pspConfig.logoImagePSP ?? pspConfig.logoPSP

  const renderStep = () => {
    if (step === 0) {
      return (
        <Typography variant='body1' sx={{fontStyle: 'italic', fontWeight: 700, color: NEUTRAL_SCALE[900]}}>
          <FormattedMessage id='warningFirstLogin' />
        </Typography>
      )
    }
    if (step === 1) {
      return (
        <Stack spacing={3}>
          <LegalContent html={firstLogin.termsAndConditions ?? ''} />
          <Card
            title={(
              <Typography variant="h3" sx={{color: NEUTRAL_SCALE[900], fontWeight: 600, fontSize: 16}}>
                <FormattedMessage id='cookiePolicy' />
              </Typography>
            )}
          >
            <Typography variant='body2' sx={{color: NEUTRAL_SCALE[900]}}>
              {firstLogin.cookieText}
              {firstLogin.cookieFile?.file && (
                <>
                  {' '}
                  <Typography
                    component='span'
                    onClick={downloadCookieFile}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: 'inherit',
                    }}
                  >
                    {'cookie policy.'}
                  </Typography>
                </>
              )}
            </Typography>
          </Card>
        </Stack>
      )
    }
    return (
      <Stack spacing={3}>
        <LegalContent html={firstLogin.dataProcessingInfo ?? ''} />
        <Typography variant='body1' sx={{fontWeight: 700}}>
          {'CONSENSO AL TRATTAMENTO DEI DATI PERSONALI'}
        </Typography>
        {firstLogin.consentCheckboxPreText && (
          <Typography variant='body2'>{firstLogin.consentCheckboxPreText}</Typography>
        )}
        <Stack spacing={2}>
          {options.map(opt => (
            <FormControl key={opt.value} error={!!errors[opt.value]} component='fieldset'>
              <Typography variant='body2' sx={{color: NEUTRAL_SCALE[900], mb: 0.5, fontWeight: 500}}>
                {opt.label}
              </Typography>
              <RadioGroup
                value={values[opt.value] ?? ''}
                onChange={(_, v) => {
                  setValues(prev => ({...prev, [opt.value]: v}))
                  setErrors(prev => ({...prev, [opt.value]: ''}))
                }}
              >
                {CONSENT_OPTIONS.map(co => (
                  <FormControlLabel
                    key={co.value}
                    value={co.value}
                    control={<Radio />}
                    label={co.label}
                  />
                ))}
              </RadioGroup>
              {errors[opt.value] && <FormHelperText>{errors[opt.value]}</FormHelperText>}
            </FormControl>
          ))}
        </Stack>
        {firstLogin.consentCheckboxPostText && (
          <Typography variant='body2'>{firstLogin.consentCheckboxPostText}</Typography>
        )}
      </Stack>
    )
  }

  return (
    <Box sx={{minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#fff'}}>
      <Box
        sx={{
          height: {xs: HEADER_HEIGHT_MOBILE, md: HEADER_HEIGHT_DESKTOP},
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          px: {xs: 2, md: 3},
          flexShrink: 0,
          width: '100%',
          zIndex: 15,
        }}
      >
        {logoSrc && (
          <Box
            component='img'
            alt='logo'
            src={`data:image/png;base64,${logoSrc}`}
            sx={{
              height: 32,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      <Box sx={{flex: 1, width: '100%', py: {xs: 3, md: 8}, px: {xs: 2, md: 6}}}>
        <Box sx={{maxWidth: 880, mx: 'auto'}}>
          <Stack spacing={{xs: 3, md: 4}}>
            {renderStep()}

            <Box sx={{display: 'flex', justifyContent: {xs: 'stretch', md: 'flex-end'}}}>
              <Button
                variant='primary'
                onClick={step === 2 ? handleSubmit : handleNext}
                loading={isSubmitting}
                sx={{width: {xs: '100%', md: 'auto'}, minWidth: {md: 160}}}
              >
                {'Accetto'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
